import { useAlert } from '../contexts/AlertContext';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, ChevronLeft, ExternalLink, Loader2, Pencil } from 'lucide-react';
import { projectApi } from '../mocks/projectApi';
import {
  PROJECT_STEPS,
  Project,
  ProjectStepKey,
  FinancePlan,
  Outcome,
  STAGE_STYLE,
  STAGE_TO_STEP,
  reachedIndex,
  PpeInput,
} from '../types/project';
import { FinancePlanTab } from './FinancePlanTab';
import { GateTab } from './GateTab';
import { ProposalTab } from './ProposalTab';
import { OutputTab } from './OutputTab';
import { RfpPanel } from '../components/proposal/RfpPanel';
import { ErrorBanner, errorText } from '../components/ErrorBanner';
import { ConfirmButton } from '../components/ConfirmButton';
import { ScoreTablesPanel } from '../components/proposal/ScoreTablesPanel';
import { BidSummaryPanel } from '../components/BidSummaryPanel';
import { DriveFolderPanel } from '../components/DriveFolderPanel';
import { IntakeCard } from '../components/intake/IntakeCard';
import { QuotePanel } from '../components/billing/QuotePanel';
import { SubmissionPanel } from '../components/submission/SubmissionPanel';
import { intakeApi } from '../services/intakeApi';
import { apiClient } from '../services/api';
import { rfpApi } from '../services/rfpApi';

/** 마감 일시를 사람이 읽는 꼴로 — 스냅샷에는 UTC ISO(`2026-10-01T01:00:00.000Z`)로 들어 있다 */
function formatClosing(v?: string | null): { text: string; closed: boolean } {
  if (!v) return { text: '—', closed: false };
  const d = new Date(v);
  if (isNaN(d.getTime())) return { text: v, closed: false };
  const p = (n: number) => String(n).padStart(2, '0');
  return {
    text: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`,
    closed: d.getTime() < Date.now(),
  };
}


/**
 * 프로젝트 상세 — 건 하나가 공고에서 산출물까지 지나간다.
 *
 * 탭이 아니라 **흐름표**다 (2026-09-20). 지나온 칸은 체크, 지금 칸은 색,
 * 아직 안 온 칸은 흐리게. `수주 판정`은 단계가 아니라 갈림길이라 점선이다.
 */
export const ProjectDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useAlert();
  /* 어느 칸을 열지 밖에서 정할 수 있다 — 대시보드가 「PPE 미반영」을 누르면
   * `?step=gate` 로 보낸다. 그러지 않으면 지금 단계(수행)가 열려서
   * 정작 눌러야 할 `PPE 반영` 버튼이 안 보인다. */
  const [params] = useSearchParams();
  const asked = params.get('step') as ProjectStepKey | null;
  const valid = asked && PROJECT_STEPS.some((x) => x.key === asked) ? asked : null;

  const [step, setStep] = useState<ProjectStepKey | null>(valid);
  const [project, setProject] = useState<Project | null>(null);
  const [plan, setPlan] = useState<FinancePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /* 저장 · 확정 · 판정 · 반영 · 완료가 실패하면 여기 — 전엔 버튼만 돌아와 된 줄 알았다(#107) */
  const [actionError, setActionError] = useState<string | null>(null);
  /* 조회 실패 — 없는 건과 다르다 */
  const [loadError, setLoadError] = useState<string | null>(null);
  /* 정보 고치기(이름 · 고객사 · PM · 마감) — 전엔 PM 을 넣을 곳이 없고 오타도 못 고쳤다(#111) */
  const [editing, setEditing] = useState(false);
  const [info, setInfo] = useState({ name: '', clientName: '', pmName: '', dueAt: '' });

  /* 실패와 「없는 건」을 구별한다(2026-10-23) — 둘 다 「찾을 수 없습니다」로 보여 서버가 죽은 줄 몰랐다 */
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const b = await projectApi.get(Number(id));
      setProject(b ?? null);
      if (b) {
        setPlan(await projectApi.getPlan(b.id));
        setStep((s) => s ?? valid ?? STAGE_TO_STEP[b.stage]);
      }
    } catch (e) {
      setLoadError(`프로젝트를 불러오지 못했습니다 — ${errorText(e)}`);
    } finally {
      setLoading(false);
    }
  }, [id, valid]);

  useEffect(() => { load(); }, [load]);



  if (loading && !project) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> 불러오는 중...
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 lg:p-8">
        {loadError ? (
          <ErrorBanner message={loadError} onRetry={load} />
        ) : (
          <p className="text-sm text-slate-500">프로젝트를 찾을 수 없습니다.</p>
        )}
      </div>
    );
  }

  const reached = reachedIndex(project);
  const current = step ?? STAGE_TO_STEP[project.stage];

  /** 처리 하나 — 실패하면 위에 띄운다. 성공했는지는 돌려준다(저장 표시용) */
  const act = async (what: string, fn: () => Promise<void>): Promise<boolean> => {
    setSaving(true);
    setActionError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setActionError(`${what} — ${errorText(e)}`);
      return false;
    } finally { setSaving(false); }
  };

  const savePlan = async () => {
    if (!plan) return false;
    return act('재무계획을 저장하지 못했습니다', async () => { await projectApi.savePlan(plan); });
  };

  const confirmPlan = async () => {
    if (!plan) return;
    await act('재무계획을 확정하지 못했습니다', async () => {
      await projectApi.savePlan(plan);
      setPlan(await projectApi.confirmPlan(plan.projectId));
      await load();
      setStep('proposal');
    });
  };

  /* 확정 해제 — 제안가 오타를 고칠 길이 없었다(2026-10-23). 판정 전에만 되고, 단계는 그대로 둔다 */
  const unconfirmPlan = async () => {
    if (!plan) return;
    await act('재무계획 확정을 해제하지 못했습니다', async () => {
      setPlan(await projectApi.unconfirmPlan(plan.projectId));
      await load();
    });
  };

  const decide = async (outcome: Outcome, contractAmount: number | null) => {
    await act(`${outcome} 처리를 하지 못했습니다`, async () => {
      await projectApi.decide(project.id, outcome, contractAmount);
      await load();
    });
  };

  const registerPpe = async (input: PpeInput) => {
    await act('admin PPE 반영을 하지 못했습니다 — admin 에는 아무것도 생기지 않았거나, 생겼다면 번호로 「잇기」를 합니다', async () => {
      const r = await projectApi.registerPpe(project.id, input);
      await load();
      // 프로젝트는 생겼는데 PPE 채우기만 실패했으면 알린다 — 「PPE 채우기」로 다시
      if (r.ppe && !r.ppe.filled && !('adminProjectId' in input)) showToast(`admin 프로젝트는 만들었지만 PPE 를 채우지 못했습니다 — ${r.ppe.note ?? ''}`, 'warning');
      else if (r.ppe?.filled) showToast('admin 에 프로젝트와 PPE(입금 일정 · 투입인건비)를 반영했습니다', 'success');
    });
  };

  /** 나라장터 결과 지금 확인 — 수주/실주 · PPE 자동 반영 */
  const checkBid = async () => {
    await act('나라장터 결과를 확인하지 못했습니다', async () => {
      await projectApi.checkBidResult(project.id);
      await load();
    });
  };

  /** 이미 이어진 admin 프로젝트의 빈 PPE 를 재무계획 숫자로 채운다 */
  const fillPpe = async () => {
    await act('PPE 를 채우지 못했습니다', async () => {
      await projectApi.fillPpe(project.id);
      showToast('admin PPE 에 재무계획 숫자를 채웠습니다 — Cash Flow · CR 이 따라 움직입니다', 'success');
    });
  };

  /** 검토 끝 — 재무계획으로. 검토 메모는 칸을 벗어날 때 이미 저장됐다 */
  const finishReview = async () => {
    await act('재무계획으로 넘기지 못했습니다', async () => {
      await projectApi.moveStage(project.id, '재무계획');
      await load();
      setStep('plan');
    });
  };

  /** 수행 완료 — 종료로 보낸다. 산출물은 아카이브에 남는다 */
  const complete = async () => {
    await act('완료 처리를 하지 못했습니다', async () => {
      await projectApi.moveStage(project.id, '종료');
      await load();
      setStep('output');
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <button
        onClick={() => navigate('/projects')}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2"
      >
        <ChevronLeft size={14} /> 프로젝트
      </button>

      <div className="flex items-start justify-between gap-4 mb-4">
        {editing ? (
          /* 카드로 감싸고 제목을 단다 — 회색 칸 이름이 한 줄로 늘어서 표 머리(칼럼)처럼 보였다. 마감은 시각까지 보이게 넓힌다 */
          <div className="flex-1 bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-slate-900 mb-3">기본 정보 고치기</p>
          <div className="grid gap-2 sm:grid-cols-[2fr_1fr_140px_220px] items-end">
            {([['name', '프로젝트명'], ['clientName', '고객사'], ['pmName', 'PM']] as const).map(([k, label]) => (
              <label key={k} className="text-xs text-slate-500">{label}
                <input value={info[k]} onChange={(e) => setInfo({ ...info, [k]: e.target.value })}
                  className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </label>
            ))}
            <label className="text-xs text-slate-500">마감
              <input type="datetime-local" value={info.dueAt} onChange={(e) => setInfo({ ...info, dueAt: e.target.value })}
                className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </label>
            <div className="sm:col-span-4 flex items-center gap-2">
              <button
                disabled={saving || !info.name.trim() || !info.clientName.trim()}
                onClick={async () => {
                  const ok = await act('정보를 저장하지 못했습니다', async () => {
                    await apiClient.patch(`/projects/${project.id}`, {
                      name: info.name.trim(), clientName: info.clientName.trim(), pmName: info.pmName.trim(),
                      dueAt: info.dueAt ? `${info.dueAt}:00+09:00` : '',
                    });
                    await load();
                  });
                  if (ok) setEditing(false);
                }}
                className="px-4 py-1.5 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
              >저장</button>
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100">취소</button>
              {/* 지우기는 공고 없이 만든 건만, 판정 · PPE 전에 — 공고에서 담은 건은 「참여 안 함」 */}
              {!project.bid && !project.outcome && !project.ppeRegisteredAt && (
                <ConfirmButton
                  onConfirm={() => act('지우지 못했습니다', async () => { await apiClient.delete(`/projects/${project.id}`); navigate('/projects'); })}
                  ask="한 번 더 — 재무계획 · 제안서 · 첨부까지 지웁니다"
                  className="ml-auto px-3 py-1.5 rounded-lg text-sm text-red-600 hover:bg-red-50"
                >프로젝트 지우기</ConfirmButton>
              )}
            </div>
          </div>
          </div>
        ) : (
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
            {project.name}
            <button
              onClick={() => { setInfo({ name: project.name, clientName: project.clientName, pmName: project.pmName ?? '', dueAt: (project.dueAt || '').replace(' ', 'T').slice(0, 16) }); setEditing(true); }}
              title="이름 · 고객사 · PM · 마감 고치기"
              className="ml-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            ><Pencil size={14} /></button>
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {project.clientName}{project.pmName ? ` · PM ${project.pmName}` : ' · PM 없음'}{project.dueAt ? ` · 마감 ${project.dueAt}` : ''}
          </p>
        </div>
        )}
        {/* 고치는 동안은 단계 표시를 숨긴다 — 칸 이름 옆에 놓여 다섯 번째 칼럼처럼 보였다 */}
        {!editing && <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STAGE_STYLE[project.stage]}`}>
            {project.stage}
          </span>
          {project.outcome === '실주' && (
            <span className="rounded-full px-2.5 py-1 text-xs font-semibold bg-red-50 text-red-600">실주</span>
          )}
        </div>}
      </div>

      {/* 흐름표 */}
      <nav className="flex items-center overflow-x-auto border-b border-slate-200 mb-5 pb-3.5">
        {PROJECT_STEPS.map((s, i) => {
          const done = i < reached;
          const on = s.key === current;
          const gate = s.key === 'gate';
          // 판정은 단계가 아니라 갈림길이라 번호를 쓰지 않는다 — 뒤 칸이 당겨진다
          const no = PROJECT_STEPS.slice(0, i + 1).filter((x) => x.key !== 'gate').length;
          return (
            <React.Fragment key={s.key}>
              {i > 0 && (
                <span className={`px-1.5 flex-shrink-0 ${i <= reached ? 'text-emerald-500' : 'text-slate-200'}`}>→</span>
              )}
              <button
                onClick={() => setStep(s.key)}
                className="flex items-center gap-2 px-1 py-1.5 whitespace-nowrap flex-shrink-0"
              >
                <span
                  className={`w-[22px] h-[22px] rounded-full grid place-items-center text-xs font-bold border-[1.5px] ${
                    gate ? 'border-dashed' : ''
                  } ${
                    on
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : done
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-600'
                        : 'bg-white border-slate-200 text-slate-400'
                  }`}
                >
                  {done ? <Check size={12} strokeWidth={3} /> : gate ? '?' : no}
                </span>
                <span className={`text-sm ${
                  on ? 'text-indigo-600 font-semibold' : done ? 'text-slate-700 font-medium' : 'text-slate-400 font-medium'
                }`}>
                  {s.label}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      <DriveFolderPanel project={project} />
      <ErrorBanner message={actionError} onClose={() => setActionError(null)} className="mb-3" />
      <div>
        <Body
          step={current}
          project={project}
          plan={plan}
          setPlan={setPlan}
          savePlan={savePlan}
          confirmPlan={confirmPlan}
          unconfirmPlan={unconfirmPlan}
          finishReview={finishReview}
          decide={decide}
          registerPpe={registerPpe}
          fillPpe={fillPpe}
          checkBid={checkBid}
          complete={complete}
          saving={saving}
        />
      </div>

    </div>
  );
};

const Body: React.FC<{
  step: ProjectStepKey;
  project: Project;
  plan: FinancePlan | null;
  setPlan: (p: FinancePlan) => void;
  savePlan: () => Promise<boolean>;
  confirmPlan: () => Promise<void>;
  /** 확정 해제 — 판정 전에만 */
  unconfirmPlan: () => Promise<void>;
  finishReview: () => Promise<void>;
  decide: (o: Outcome, amount: number | null) => Promise<void>;
  registerPpe: (input: PpeInput) => Promise<void>;
  fillPpe: () => Promise<void>;
  checkBid: () => Promise<void>;
  complete: () => Promise<void>;
  saving: boolean;
}> = ({ step, project, plan, setPlan, savePlan, confirmPlan, unconfirmPlan, finishReview, decide, registerPpe, fillPpe, checkBid, complete, saving }) => {
  if (step === 'bid') {
    if (!project.bid) {
      return (
        <div className="space-y-4">
          <Empty text="공고에서 넘어온 프로젝트가 아닙니다 — 직접 등록했습니다." />
          <RfpPanel projectId={project.id} hasBid={false} />
        </div>
      );
    }
    const b = project.bid;
    return (
      <div className="space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 px-4 py-4">
        <dl className="grid grid-cols-[92px_1fr] gap-y-2 gap-x-4 text-sm">
          <dt className="text-slate-500">출처</dt>
          <dd className="font-medium text-slate-900">{b.source} · {b.bizDiv}{b.classification ? ` · ${b.classification}` : ''}</dd>
          <dt className="text-slate-500">공고번호</dt>
          <dd className="font-medium text-slate-900">{b.bidNtceNo} · {Number(b.bidNtceOrd) > 0 ? `${Number(b.bidNtceOrd)}차 변경` : '원공고'}</dd>
          <dt className="text-slate-500">발주기관</dt>
          <dd className="font-medium text-slate-900">{b.ntceInsttNm}</dd>
          <dt className="text-slate-500">{b.closingKind}</dt>
          <dd className="font-medium text-slate-900">
            {formatClosing(b.closingAt).text}
            {formatClosing(b.closingAt).closed && <span className="ml-2 rounded px-1.5 py-0.5 text-xs font-medium bg-red-50 text-red-600">{b.closingKind} 지남</span>}
          </dd>
          <dt className="text-slate-500">추정가격</dt>
          <dd className="font-medium text-slate-900">{Number(b.presmptPrce) > 0 ? `₩${Number(b.presmptPrce).toLocaleString()}` : '—'}</dd>
        </dl>
        {b.bidNtceDtlUrl && (
          <a
            href={b.bidNtceDtlUrl} target="_blank" rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
          >
            원문 보기 <ExternalLink size={12} />
          </a>
        )}
        <p className="mt-3 text-xs text-slate-400">
          담을 때 공고 내용을 베껴 둡니다 — 나라장터는 마감된 공고를 돌려주지 않습니다.
        </p>
      </div>
      <RfpPanel projectId={project.id} hasBid />
      </div>
    );
  }

  if (step === 'review') {
    return <ReviewStep project={project} onFinish={finishReview} saving={saving} />;
  }

  if (step === 'plan') {
    return plan ? (
      <>
        <FinancePlanTab
          plan={plan}
          bid={project.bid}
          onChange={setPlan}
          onSave={savePlan}
          onConfirm={confirmPlan}
          onUnconfirm={unconfirmPlan}
          decided={!!project.outcome}
          saving={saving}
        />
        {/* 견적서 — 저장한 재무계획의 사람 · MM 을 읽는다(작업 6) */}
        <QuotePanel projectId={project.id} />
      </>
    ) : <Empty text="재무계획을 불러오지 못했습니다." />;
  }

  if (step === 'proposal') {
    return (
      <>
        <ProposalTab project={project} plan={plan} />
        {/* 제출서류 묶음 — 요구 서류 ↔ 회사서류(작업 7, #125 · #159) */}
        <SubmissionPanel projectId={project.id} />
      </>
    );
  }

  if (step === 'gate') {
    return (
      <GateTab
        project={project}
        plan={plan}
        onDecide={decide}
        onRegisterPpe={registerPpe}
        onFillPpe={fillPpe}
        onCheckBid={checkBid}
        saving={saving}
      />
    );
  }


  if (step === 'output') {
    return <OutputTab project={project} onComplete={complete} saving={saving} />;
  }

  const label = PROJECT_STEPS.find((s) => s.key === step)?.label;
  return <Empty text={`${label} 은 아직 만들지 않았습니다.`} />;
};

/**
 * 검토 — 공고를 보고 할지 정하는 첫 단계. 메모는 칸을 벗어날 때 저장하고, 저장 결과를 보인다(실패를 삼키지 않는다).
 * 「검토 완료」는 재무계획으로 넘긴다. AI 는 이 메모를 제안서 「사업 이해」의 근거로 읽는다.
 */
const ReviewStep: React.FC<{ project: Project; onFinish: () => Promise<void>; saving: boolean }> = ({ project, onFinish, saving }) => {
  const navigate = useNavigate();
  const [memo, setMemo] = useState(project.memo);
  const [memoState, setMemoState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [memoError, setMemoError] = useState<string | null>(null);
  const inReview = project.stage === '검토';
  const { showConfirm } = useAlert();
  const [skipError, setSkipError] = useState<string | null>(null);
  const [skipping, setSkipping] = useState(false);
  /* 배점표 · 제안서가 첨부를 읽는다. 검토에 들어오면 없을 때 한 번 받는다 —
     예전엔 「공고」 칸의 첨부 받기를 먼저 눌러야 했는데 그 안내가 없었다(2026-10-06) */
  const [rfpNote, setRfpNote] = useState<string | null>(null);
  const [evalKey, setEvalKey] = useState(0);
  /* 접수서 — 공고에서 담은 건은 담을 때 만든다. 2026-10-22 전에 담은 건은 여기서 한 번 만든다(AI 안 씀).
     이미 있으면 서버가 빈 칸만 공고 요약으로 채운다 — 담은 뒤 요약을 만들면 요약이 끝날 때 다시 부른다(2026-11-01) */
  const [intake, setIntake] = useState(project.intake ?? null);
  const refillIntake = useCallback(() => {
    if (!project.bid) return;
    intakeApi.fromBid(project.id).then((f) => { if (f) setIntake(f); }).catch(() => undefined);
  }, [project.id, project.bid]);
  useEffect(() => { refillIntake(); }, [refillIntake]);

  useEffect(() => {
    if (!project.bid || !inReview) return;
    let gone = false;
    (async () => {
      try {
        const files = await rfpApi.list(project.id);
        if (files.length > 0) return;
        setRfpNote('제안요청서 첨부를 받는 중…');
        await rfpApi.sync(project.id);
        if (gone) return;
        setRfpNote(null);
        setEvalKey((k) => k + 1);
      } catch (e) {
        if (!gone) setRfpNote(`첨부를 받지 못했습니다 — ${(e as Error).message}. 「공고」 칸에서 다시 받을 수 있습니다`);
      }
    })();
    return () => { gone = true; };
  }, [project.id, project.bid, inReview]);

  /** 저장했으면 true. 바뀐 게 없어도 true */
  const saveMemo = async (): Promise<boolean> => {
    if (memo === project.memo && memoState !== 'error') return true;
    setMemoState('saving');
    try {
      await projectApi.updateMemo(project.id, memo);
      project.memo = memo; // 다시 불러오지 않고 같은 값이면 저장을 건너뛰기 위한 기준
      setMemoState('saved');
      setMemoError(null);
      return true;
    } catch (e) {
      setMemoState('error');
      setMemoError(e instanceof Error ? e.message : '메모를 저장하지 못했습니다');
      return false;
    }
  };

  /* 메모를 먼저 저장하고 넘긴다 — 쓰다 바로 누르면 저장과 단계 이동이 겹쳤다 */
  const finish = async () => {
    if (await saveMemo()) await onFinish();
  };

  const skip = async () => {
    setSkipping(true);
    setSkipError(null);
    try {
      await saveMemo();
      await apiClient.post(`/bidding/projects/${project.id}/skip`, {});
      navigate('/projects');
    } catch (e) {
      setSkipError((e as Error).message);
      setSkipping(false);
    }
  };

  return (
    <div className="space-y-4">
      {intake && (
        <IntakeCard
          key={intake.receivedAt + project.id}
          projectId={project.id}
          intake={intake}
          hasBid={!!project.bid}
          onSaved={(f) => {
            setIntake(f);
            // 접수서 「메모」는 서버가 검토 메모에도 넣는다 — 화면도 맞춘다
            if (f.memo && f.memo !== memo) { setMemo(f.memo); project.memo = f.memo; setMemoState('idle'); }
          }}
        />
      )}
      {/* 판단 재료(적합도 · 마감 · AI 요약)는 공고 건만 — 직접 만든 건은 「적합도 0 · 근거 없음」만 떠서 뺐다(2026-10-22) */}
      {inReview && project.bid && <ReviewBrief project={project} onSummaryDone={refillIntake} />}

      <div className="bg-white rounded-lg border border-slate-200">
        <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">검토 메모</h2>
        <div className="px-4 py-3">
          <textarea
            value={memo}
            onChange={(e) => { setMemo(e.target.value); setMemoState('idle'); }}
            onBlur={saveMemo}
            placeholder="우리 분야인지, 실적이 있는지, 누가 볼지"
            className="w-full h-28 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-indigo-400 focus:outline-none resize-none"
          />
          <p className={`mt-1.5 text-xs ${memoState === 'error' ? 'text-red-600' : 'text-slate-400'}`}>
            {memoState === 'saving' ? '저장 중...'
              : memoState === 'saved' ? '저장했습니다.'
                : memoState === 'error' ? `저장 실패 — ${memoError}`
                  : ''}
          </p>
        </div>
      </div>

      {rfpNote && <p className="text-xs text-slate-500">{rfpNote}</p>}
      {/* 공고 없이 만든 건은 제안요청서를 여기서 올린다 — 「공고」 칸에 숨어 있었다(#111) */}
      {!project.bid && <RfpPanel projectId={project.id} hasBid={false} onChanged={() => setEvalKey((k) => k + 1)} />}
      {/* 배점표는 첨부 표를 그대로 보인다 — AI 로 뽑지 않는다. 없으면 칸이 없다(2026-10-16) */}
      <ScoreTablesPanel key={evalKey} projectId={project.id} />

      <div className="flex flex-wrap items-center justify-end gap-3">
        {inReview ? (
          <>
            <button onClick={async () => await showConfirm('보드에서 빼고 「미참여」로 둡니다. 계속할까요?') && skip()} disabled={skipping}
              className="px-3 py-2 rounded-lg text-sm text-slate-600 border border-slate-300 hover:bg-slate-50 disabled:opacity-40">
              {skipping ? '빼는 중…' : '참여 안 함'}
            </button>
            {skipError && <span className="text-xs text-red-600">{skipError}</span>}
            <span className="flex-1" />
            {/* PM 이 없으면 수주 때 PPE 를 자동 반영하지 못한다(admin 은 PM 이 꼭 있어야 한다) — 제목 옆 ✏️ 로 지정 */}
            {!project.pmName && <span className="text-xs text-amber-700 bg-amber-50 rounded-full px-2.5 py-1">PM 미지정</span>}
            <button
              onClick={finish}
              disabled={saving || memoState === 'saving' || skipping}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              검토 완료 · 재무계획으로
            </button>
          </>
        ) : (
          <span className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
            검토를 지났습니다 — 지금 단계는 {project.stage}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * 검토의 판단 재료 — 마감 · AI 요약. 공고가 지워져도 AI 요약은 따로 남아 있다.
 * 적합도는 2026-10-22 화면에서 뺐다 — 사용자에게 필요 없다고 판단
 */
const ReviewBrief: React.FC<{ project: Project; onSummaryDone?: () => void }> = ({ project, onSummaryDone }) => {
  const b = project.bid;
  const closing = formatClosing(b?.closingAt ?? project.dueAt);
  const price = Number(b?.presmptPrce || 0);
  const facts = [
    `${b?.closingKind ?? '마감'} ${closing.text}${closing.closed ? ' · 지남' : ''}`,
    price ? `추정 ${price >= 1e8 ? `${(price / 1e8).toFixed(1).replace(/\.0$/, '')}억` : `${Math.round(price / 1e4).toLocaleString('ko-KR')}만`}` : '',
    b?.classification ?? '',
  ].filter(Boolean);
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-4 py-4 space-y-3">
      <h2 className="text-sm font-semibold text-slate-900">판단 재료</h2>
      {closing.closed && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {b?.closingKind ?? '마감'}이 지났습니다({closing.text})
        </p>
      )}
      {b?.bidNtceNo && <BidSummaryPanel bidNtceNo={b.bidNtceNo} facts={facts} onDone={onSummaryDone} />}
    </div>
  );
};

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div className="bg-white rounded-lg border border-slate-200 py-16 text-center">
    <p className="text-sm text-slate-500">{text}</p>
  </div>
);
